import { useCallback, useEffect, useRef, useState } from 'react';

export const TABLE_FORM_DRAWER_DEFAULT_WIDTH = 760;
export const TABLE_FORM_DRAWER_MIN_WIDTH = 480;
const TABLE_FORM_DRAWER_VIEWPORT_GAP = 32;

const viewportWidth = () => (typeof window === 'undefined' ? TABLE_FORM_DRAWER_DEFAULT_WIDTH : window.innerWidth);

export const tableFormDrawerStorageKey = (username: string) => `user-${username || 'anonymous'}.tableFormDrawerWidth`;

export const tableFormDrawerWidthBounds = (viewport: number) => {
    const min = Math.min(TABLE_FORM_DRAWER_MIN_WIDTH, viewport);
    return { min, max: Math.max(min, viewport - TABLE_FORM_DRAWER_VIEWPORT_GAP) };
};

export const clampTableFormDrawerWidth = (width: number, viewport: number) => {
    const { min, max } = tableFormDrawerWidthBounds(viewport);
    return Math.min(max, Math.max(min, width));
};

const readStoredWidth = (storageKey: string) => {
    try {
        const stored = Number(localStorage.getItem(storageKey));
        return Number.isFinite(stored) && stored > 0 ? stored : TABLE_FORM_DRAWER_DEFAULT_WIDTH;
    } catch {
        return TABLE_FORM_DRAWER_DEFAULT_WIDTH;
    }
};

const persistWidth = (storageKey: string, width: number) => {
    try {
        localStorage.setItem(storageKey, String(Math.round(width)));
    } catch {
        // localStorage может быть недоступен в приватном режиме.
    }
};

interface ITableFormDrawerWidth {
    width: number;
    /** Ручку тащат: курсор к этому моменту ушёл с неё, а подсветка держится. */
    resizing: boolean;
    startResize: (clientX: number) => void;
    resizeBy: (delta: number) => void;
    resetWidth: () => void;
}

export const useTableFormDrawerWidth = (username: string): ITableFormDrawerWidth => {
    const storageKey = tableFormDrawerStorageKey(username);
    const [viewport, setViewport] = useState(viewportWidth);
    const [preferredWidth, setPreferredWidth] = useState(() => readStoredWidth(storageKey));
    const [resizing, setResizing] = useState(false);
    const width = clampTableFormDrawerWidth(preferredWidth, viewport);
    const widthRef = useRef(width);
    const dragCleanupRef = useRef<(() => void) | null>(null);
    widthRef.current = width;

    useEffect(() => {
        setPreferredWidth(readStoredWidth(storageKey));
    }, [storageKey]);

    useEffect(() => {
        const onResize = () => setViewport(viewportWidth());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(
        () => () => {
            dragCleanupRef.current?.();
        },
        [],
    );

    const setWidth = useCallback(
        (nextWidth: number, save: boolean) => {
            const next = clampTableFormDrawerWidth(nextWidth, viewportWidth());
            widthRef.current = next;
            setPreferredWidth(next);
            if (save) persistWidth(storageKey, next);
        },
        [storageKey],
    );

    const startResize = useCallback(
        (clientX: number) => {
            dragCleanupRef.current?.();
            const startX = clientX;
            const startWidth = widthRef.current;
            const previousCursor = document.body.style.cursor;
            const previousUserSelect = document.body.style.userSelect;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            const cleanup = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onEnd);
                window.removeEventListener('pointercancel', onEnd);
                document.body.style.cursor = previousCursor;
                document.body.style.userSelect = previousUserSelect;
                dragCleanupRef.current = null;
                setResizing(false);
            };
            const onMove = (event: PointerEvent) => setWidth(startWidth + startX - event.clientX, false);
            const onEnd = () => {
                persistWidth(storageKey, widthRef.current);
                cleanup();
            };

            dragCleanupRef.current = cleanup;
            setResizing(true);
            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onEnd);
            window.addEventListener('pointercancel', onEnd);
        },
        [setWidth, storageKey],
    );

    const resizeBy = useCallback((delta: number) => setWidth(widthRef.current + delta, true), [setWidth]);
    const resetWidth = useCallback(() => setWidth(TABLE_FORM_DRAWER_DEFAULT_WIDTH, true), [setWidth]);
    return { width, resizing, startResize, resizeBy, resetWidth };
};
